import { useState, useEffect, createContext, useContext, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { clearImpersonatedAgency } from "@/lib/admin-impersonation";
import { sweepOtherUsers } from "@/lib/onboarding-storage";

interface SignUpResult {
  error: Error | null;
  data: { user: User | null } | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, metadata?: { first_name?: string; last_name?: string; user_type?: string; invite_code?: string }, redirectTo?: string) => Promise<SignUpResult>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signInWithApple: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Track the previously authenticated user so we can detect actual
    // account switches. SIGNED_IN fires on token refresh / tab focus too,
    // not just on real sign-in - clearing impersonation on every SIGNED_IN
    // would wipe an admin's "Manage agency" selection mid-session.
    let previousUserId: string | null = null;

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        const newUserId = session?.user?.id ?? null;

        if (event === 'SIGNED_OUT') {
          clearImpersonatedAgency();
          sweepOtherUsers(null);
        } else if (event === 'SIGNED_IN' && previousUserId && previousUserId !== newUserId) {
          // Account actually switched on this device - drop the prior admin's
          // impersonation and onboarding flags so they can't leak.
          clearImpersonatedAgency();
          sweepOtherUsers(newUserId);
        }

        // Internal sign-in notice (skips Focus staff / test accounts and
        // dedupes server-side, so token refreshes never spam the inbox).
        if (event === 'SIGNED_IN' && session?.user) {
          setTimeout(() => {
            supabase.rpc('record_signin_event').then(({ error }) => {
              if (error) console.warn('record_signin_event failed', error.message);
            });
          }, 0);
        }

        previousUserId = newUserId;
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        // Set role for Focus AI chatbot on login
        if (session?.user) {
          // Check profile + admin role to determine behavior
          supabase
            .from('profiles')
            .select('user_type')
            .eq('id', session.user.id)
            .maybeSingle()
            .then(({ data }) => {
              if (data?.user_type === 'agency') {
                localStorage.setItem('focusai_user_role', 'agent');
              } else {
                localStorage.setItem('focusai_user_role', 'client');
              }
            });

          // Defensive: if the current user is NOT a platform admin, ensure
          // no impersonation state can ever apply to them.
          supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', session.user.id)
            .eq('role', 'admin')
            .maybeSingle()
            .then(({ data }) => {
              if (!data) {
                clearImpersonatedAgency();
              }
            });
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, metadata?: { first_name?: string; last_name?: string; user_type?: string; invite_code?: string }, redirectTo?: string): Promise<SignUpResult> => {
    const redirectUrl = redirectTo || `${window.location.origin}/`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: metadata
      }
    });
    return { 
      error: error as Error | null,
      data: data ? { user: data.user } : null
    };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error: error as Error | null };
  };

  const signInWithGoogle = async () => {
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: `${window.location.origin}/auth`,
    });
    return { error: result.error ? (result.error instanceof Error ? result.error : new Error(String(result.error))) : null };
  };

  const signInWithApple = async () => {
    const result = await lovable.auth.signInWithOAuth("apple", {
      redirect_uri: `${window.location.origin}/auth`,
    });
    return { error: result.error ? (result.error instanceof Error ? result.error : new Error(String(result.error))) : null };
  };

  const signOut = async () => {
    try {
      localStorage.removeItem('focusai_user_role');
      clearImpersonatedAgency();
      await supabase.auth.signOut({ scope: 'local' });
    } catch {
      // If server-side sign out fails (e.g. session already expired),
      // still clear local state
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signInWithGoogle, signInWithApple, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
